import {
  DownloadOutlined,
  MoreOutlined,
  OpenAIOutlined,
  EyeOutlined,
  DeleteOutlined,
  FilePdfOutlined,
} from '@ant-design/icons'
import AppModal from '@components/AppModal'
import ModalConfirm from '@components/AppModal/ModalConfirm'
import ButtonLink from '@components/ButtonLink'
import { AttachmentExtensionEnum } from '@components/CaseFieldUpload/Upload'
import { Flex } from '@components/Layout'
import TooltipInfo from '@components/TooltipInfo'
import { ApiService } from '@services/ApiService'
import appStore from '@stores/appStore'
import { Dropdown, Form, Spin } from 'antd'
import { FormLabelAlign } from 'antd/lib/form/interface'
import cx from 'classnames'
import { observer } from 'mobx-react'
import { useEffect, useMemo, useState } from 'react'
import ReactMarkdown from 'react-markdown'

import { AppButton } from '../AppButton'
import { AppFileUpload } from '../AppFileUpload'

import './style.scss'
import { ReactComponent as CheckDuplicateIcon } from '@assets/svg/check-duplicate.svg'

import ButtonText from '../ButtonText'

import CleanupSummaryModal from './components/CleanupSummaryModal'

interface AppFormFileUploadProps {
  hideButton?: boolean
  hideViewPdfButton?: boolean
  hideUploadArea?: boolean
  className?: string
  classNameUpload?: string
  label?: string | React.ReactNode
  suffixLabel?: string | React.ReactNode
  rules?: any
  validateStatus?: any
  help?: string | React.ReactNode
  required?: boolean
  labelAlign?: string
  labelCol?: any
  wrapperCol?: any
  dragMessages?: string
  name?: string
  type?: string[]
  description?: string
  multiUpload?: boolean
  maxSize?: number
  previewTemplate?: any
  fileList?: any[]
  disabled?: boolean
  attachmentTypeId?: string
  isViewMergeFile?: boolean
  onChange?: (files: any[], file: any) => void
  beforeUpload?: () => void
  disabledDelete?: boolean
  tenantId?: string
  tooltipMessage?: string
  onRemoveAll?: () => void
  extensions?: AttachmentExtensionEnum[]
  showStaticFileList?: boolean
}
export const AppFormFileUpload = ({
  hideUploadArea = false,
  showStaticFileList = false,
  className = '',
  classNameUpload = '',
  label,
  suffixLabel,
  rules = null,
  validateStatus = null,
  help = null,
  required = false,
  labelAlign = 'left',
  labelCol,
  wrapperCol,
  dragMessages = 'Drag and drop here or Click to browse',
  name = 'file',
  type = [],
  description = null,
  multiUpload = false,
  maxSize = 100,
  previewTemplate = null,
  fileList = [],
  disabled = false,
  attachmentTypeId = null,
  isViewMergeFile = false,
  onChange = (files, file) => { },
  beforeUpload = () => { },
  disabledDelete = false,
  tenantId = null,
  tooltipMessage = '',
  onRemoveAll = () => { },
  extensions = [],
  hideButton = false,
  hideViewPdfButton = false,
}: AppFormFileUploadProps) => {
  const [attachments, setAttachments] = useState([])
  const [isOpenAttachmentList, setIsOpenAttachmentList] = useState(false)
  const [key, setKey] = useState(Date.now())
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [currentFileIndex, setCurrentFileIndex] = useState(null)
  const [isOpenSummary, setIsOpenSummary] = useState(false)
  const [summaryTitle, setSummaryTitle] = useState('')
  const [summaryText, setSummaryText] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const isPdfFile = fileName => {
    if (!fileName) {
      return false
    }
    return fileName.toLowerCase().endsWith('.pdf')
  }

  const handleOnChange = (files, file) => {
    setAttachments(files)
    onChange(files, file)
  }

  const beforeUpload_ = () => {
    if (!!attachments.length && !multiUpload) {
      handleRemoveFile(0)
    }
    beforeUpload && beforeUpload()
  }

  const handleRemoveFile = index => {
    const newAttachments = [...attachments]
    const files = newAttachments.splice(index, 1)
    setAttachments([...newAttachments])
    onChange(newAttachments, { ...files[0], status: 'removed' })
    if (newAttachments.length === 0) {
      setIsOpenAttachmentList(false)
    }
  }

  const renderTitle = () => {
    return attachments && !!attachments.length
      ? `(${attachments.length} ${attachments.length > 1 ? 'files' : 'file'})`
      : !isViewMergeFile
        ? disabled
          ? '(No file)'
          : ``
        : '(No Merge File)'
  }

  useEffect(() => {
    setAttachments(fileList)
  }, [fileList])

  const handleDownloadFile = file => {
    ApiService.File.Download(file)
  }

  const handleRemoveAllFiles = () => {
    appStore.openConfirmModal({
      message: 'Do you want delete all files?',
      onOk: () => {
        onRemoveAll()
      },
    })
  }

  const handleSummaryDocument = async attachment => {
    if (!attachment.summaryText) {
      setIsLoading(true)
      try {
        const res = await ApiService.Attachment.SummaryDocument(attachment.id)
        attachment.summaryText = res.data
      } finally {
        setIsLoading(false)
      }
    }

    setIsOpenSummary(true)
    setSummaryText(attachment.summaryText)
    setSummaryTitle(attachment.name)
  }

  const [cleanupSummaryData, setCleanupSummaryData] = useState(null)
  const [isOpenCleanupSummary, setIsOpenCleanupSummary] = useState(false)
  const handleCleanupFile = async attachmentId => {
    setIsLoading(true)

    try {
      const resp = await ApiService.Attachment.CleanupFile(attachmentId)
      const cleanupSummary = resp.data

      setIsOpenCleanupSummary(true)
      setCleanupSummaryData(cleanupSummary)

      if (resp.data) {
        appStore.showSuccessMessage.customMessage(
          'File has been cleaned up successfully'
        )
      } else {
        appStore.showSuccessMessage.customMessage(
          'File already cleaned up. No action required'
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleOpenPDFViewer = file => {
    appStore.openPDFViewer(file.fileId, file.name || file.fileName)
  }

  const visibleExtensions = useMemo(() => {
    if (appStore.isAdminOrStaff) {
      if (!extensions) {
        return [AttachmentExtensionEnum.CLEAN_UP_FILE]
      }
      if (!extensions.includes(AttachmentExtensionEnum.CLEAN_UP_FILE)) {
        return [...extensions, AttachmentExtensionEnum.CLEAN_UP_FILE]
      }
    }
    return extensions
  }, [extensions])

  const summaryEnabled = visibleExtensions?.includes(
    AttachmentExtensionEnum.SUMMARY
  )

  const cleanupFileEnabled = visibleExtensions?.includes(
    AttachmentExtensionEnum.CLEAN_UP_FILE
  )

  const renderFileItem = (file, index, variant: 'modal' | 'static') => {
    const isStatic = variant === 'static'
    const containerClass = isStatic
      ? 'flex items-center justify-between p-2 mb-1 rounded bg-[#F3F5F7] hover:bg-[#e5e7eb] transition-colors group'
      : 'flex items-center justify-between rounded p-1 bg-[#F3F5F7] px-2 file-item-container'
    const labelClass = isStatic
      ? 'flex items-center gap-1 flex-1 overflow-hidden'
      : 'font-normal flex gap-1 items-center'
    const actionsClass = isStatic
      ? 'flex items-center gap-0 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 flex-shrink-0'
      : 'flex gap-0 item-actions items-center'
    const showActions = isStatic || !appStore.isDemoAccount
    const fileName = file.name || file.fileName
    const itemKey = isStatic ? file.fileId || file.id || index : index

    return (
      <div className={containerClass} key={itemKey}>
        <div className={labelClass}>
          {file.isCleanup && (
            <span
              title="The file has been cleaned up."
              className={isStatic ? 'mr-1 flex-shrink-0' : 'mr-1'}
            >
              <CheckDuplicateIcon
                style={{
                  width: '16px',
                  height: '16px',
                  display: 'inline-block',
                }}
              />
            </span>
          )}
          {isStatic ? (
            <>
              <span className="text-sm text-gray-700 truncate">{fileName}</span>
              {file.numberOfPages && (
                <span className="text-sm text-gray-500 flex-shrink-0">
                  {' '}
                  - {file.numberOfPages}{' '}
                  {file.numberOfPages > 1 ? 'pages' : 'page'}
                </span>
              )}
              {file.creationTime && (
                <span className="text-sm text-gray-500 flex-shrink-0">
                  {' '}
                  - {appStore.formatDateTime(file.creationTime)}
                </span>
              )}
            </>
          ) : (
            <>
              {fileName}
              {file.numberOfPages
                ? ` - ${file.numberOfPages} ${file.numberOfPages > 1 ? 'pages' : 'page'
                }`
                : ''}
              {file.creationTime
                ? ` - ${appStore.formatDateTime(file.creationTime)}`
                : ''}
            </>
          )}
        </div>

        {showActions && (
          <div className={actionsClass}>
            {summaryEnabled && isPdfFile(file.fileName) && (
              <ButtonText
                onClick={() => handleSummaryDocument(file)}
                title="View Summary"
              >
                <OpenAIOutlined style={{ width: '16px' }} />
              </ButtonText>
            )}
            {cleanupFileEnabled && !file.isCleanup && file.id && (
              <ButtonText
                onClick={() => handleCleanupFile(file.id)}
                title="Clean up the file. Remove duplicates and blank pages."
              >
                <CheckDuplicateIcon
                  style={{ width: '16px', height: '16px' }}
                />
              </ButtonText>
            )}
            {isPdfFile(file.fileName) && !hideViewPdfButton && (
              <ButtonText
                onClick={() => handleOpenPDFViewer(file)}
                title="View PDF"
              >
                <FilePdfOutlined />
              </ButtonText>
            )}
            <ButtonText
              onClick={() => handleDownloadFile(file)}
              title="Download"
            >
              <DownloadOutlined />
            </ButtonText>
            {!disabledDelete && !appStore.isContractor && (
              <ButtonText
                onClick={() => {
                  setCurrentFileIndex(index)
                  setConfirmDelete(true)
                }}
                title="Delete"
              >
                <DeleteOutlined style={isStatic ? undefined : { fill: 'red' }} />
              </ButtonText>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <Form.Item
      className={cx([className, 'form-file-upload'])}
      label={''}
      rules={rules}
      validateStatus={validateStatus}
      help={help}
      required={required}
      labelAlign={labelAlign as FormLabelAlign}
      labelCol={labelCol}
      wrapperCol={wrapperCol}
    >
      <div className={cx(['flex justify-between mb-2', classNameUpload])}>
        <span className="mt-2 w-full lg:w-auto flex items-center gap-2">
          <span>{label}</span>
          {renderTitle()}
          {tooltipMessage ? (
            <TooltipInfo text={tooltipMessage} />
          ) : (
            !multiUpload && <TooltipInfo text={'Only 1 document is allowed'} />
          )}
          {suffixLabel}
        </span>

        <Flex>
          {summaryEnabled && (
            <OpenAIOutlined
              title="Document summary is enabled"
              className="hover:text-blue-500 text-xl"
            />
          )}
          {!hideButton && (
            <>
              <AppButton
                className="rounded-md"
                disabled={!fileList.length}
                color="blue"
                onClick={() => setIsOpenAttachmentList(true)}
                title="View all"
                size="small"
              >
                <EyeOutlined />
              </AppButton>
              <AppButton
                className="rounded-md"
                disabled={!fileList.length || appStore.isDemoAccount}
                color="green"
                onClick={() => ApiService.File.DownloadAll(fileList)}
                title="Download all"
                size="small"
              >
                <DownloadOutlined width="16" height="16" />
              </AppButton>
            </>
          )}
        </Flex>
        <AppModal
          open={isOpenAttachmentList}
          width={768}
          onCancel={() => setIsOpenAttachmentList(false)}
          footer={
            <AppButton
              color="red"
              outline
              onClick={() => setIsOpenAttachmentList(false)}
            >
              Close
            </AppButton>
          }
          title={
            <Flex className="justify-between">
              <span>{renderTitle()}</span>
              {!disabledDelete &&
                attachments.length > 0 &&
                !appStore.isContractor && (
                  <ButtonLink
                    color="red"
                    className="text-base"
                    onClick={() => handleRemoveAllFiles()}
                  >
                    Remove All
                  </ButtonLink>
                )}
            </Flex>
          }
          className="view-attachment-list-modal"
        >
          <Spin spinning={isLoading}>
            <div className="py-2.5">
              {attachments.map((file, index) =>
                renderFileItem(file, index, 'modal')
              )}
            </div>
          </Spin>
        </AppModal>
      </div>
      {!disabled && !hideUploadArea && (
        <div className={showStaticFileList && attachments && attachments.length > 0 ? "border-[#d9d9d9] border rounded-md overflow-hidden" : ""}>
          <div className={showStaticFileList && attachments && attachments.length > 0 ? "p-4" : ""}>

            <AppFileUpload
              key={key}
              name={name}
              readonly={disabled || appStore.isDemoAccount}
              type={type}
              description={description ?? `(Max ${maxSize}MB)`}
              previewTemplate={previewTemplate}
              maxSize={maxSize}
              title={dragMessages}
              multiUpload={multiUpload}
              fileList={attachments}
              itemRender={() => <></>}
              attachmentTypeId={attachmentTypeId}
              onChange={handleOnChange}
              beforeUpload={beforeUpload_}
              tenantId={tenantId}
            />
            {showStaticFileList && attachments && attachments.length > 0 && (
              <div className="mt-2">
                {attachments.map((file, index) =>
                  renderFileItem(file, index, 'static')
                )}
              </div>
            )}
          </div>
        </div>
      )}
      {confirmDelete && (
        <ModalConfirm
          modalParams={{
            message: 'Do you want to delete this file?',
            onOk: () => {
              if (currentFileIndex >= 0) {
                handleRemoveFile(currentFileIndex)
              }
            },
          }}
          onCancel={() => setConfirmDelete(false)}
        />
      )}

      <AppModal
        open={isOpenSummary}
        onCancel={() => setIsOpenSummary(false)}
        width={1024}
        title={
          <span>
            File summary: <span>{summaryTitle}</span>
          </span>
        }
      >
        <ReactMarkdown className="markdown">{summaryText}</ReactMarkdown>
        <div className="rounded-lg bg-grey-100 mt-2 p-2 border border-solid border-grey-400">
          <span className="font-bold">Disclaimer:</span> Contents are generated
          by OpenAI based on stored documents. Kawaconn did not create and is
          not responsible for this summary.
        </div>
      </AppModal>

      {isOpenCleanupSummary && (
        <CleanupSummaryModal
          cleanupSummary={cleanupSummaryData}
          onClose={() => {
            setIsOpenCleanupSummary(false)
          }}
        />
      )}
    </Form.Item>
  )
}

export default observer(AppFormFileUpload)
